import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BeatTypeConsolAddService {

  constructor(private http: HttpClient) { }

  /**
   * Save BEAT Type Consol Add form data
   * @param baseUrl Base URL for API calls
   * @param formData Form data to save
   * @param globalService Global service data (globalParams)
   * @param filterParams Filter parameters
   * @param rowData Row data
   * @param userSettingsData User settings data (optional)
   * @param generalConfigData General config data (optional)
   * @returns Observable with response
   */
  saveBeatTypeConsolAddForm(
    baseUrl: string,
    formData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {},
    userSettingsData: any = {},
    generalConfigData: any = {}
  ): Observable<any> {
    
    // Get tax year for clob_setting
    const taxYearForClob = filterParams.tax_year || rowData.TAX_YEAR || globalService?.tax_year || globalService?.globalParams?.tax_year;
    
    // Get screen key for clob_setting - check generalConfigData first (like component does), then globalService
    const screenKeyForClob = generalConfigData?.globalParams?.screen_key || 
                             globalService?.screen_key || 
                             filterParams.screen_key || 
                             globalService?.globalParams?.screen_key;
    
    // Get sso_id - check userSettingsData first (like component does), then other sources
    const ssoId = userSettingsData?.user?.sso_id || 
                  globalService?.userSettingsData?.user?.sso_id || 
                  globalService?.user?.sso_id || 
                  globalService?.sso_id ||
                  filterParams.sso_id ||
                  rowData.sso_id;
    
    const clob_settings = {
      'sso_id': ssoId || '',
      'tax_year': taxYearForClob || '',
      'SCREEN_KEY': screenKeyForClob || '',
      'object_id': rowData.object_id || null
    };

    // Build params from all sources with proper fallbacks (no hardcoded values)
    const params: { [key: string]: any } = {};

    // Clob settings and data (required)
    params.clob_settings = JSON.stringify(clob_settings);
    params.clob_data = JSON.stringify(formData);
    
    // Tax year
    const taxYear = filterParams.tax_year || rowData.TAX_YEAR || globalService.tax_year || globalService.globalParams?.tax_year;
    if (taxYear !== undefined && taxYear !== null && taxYear !== '') {
      params.tax_year = taxYear;
    }
    
    // Scenario parameters
    const scenario = filterParams.scenario || rowData.SCENARIO || globalService.scenario || globalService.globalParams?.scenario;
    if (scenario !== undefined && scenario !== null && scenario !== '') {
      params.scenario = scenario;
    }
    
    // JCD key parameters
    const jcdKey = filterParams.jcd_key || rowData.JCD_KEY || globalService.jcd_key || globalService.globalParams?.jcd_key;
    if (jcdKey !== undefined && jcdKey !== null && jcdKey !== '') {
      params.jcd_key = jcdKey;
    }
    
    const jcdTaKey = filterParams.jcd_ta_key || globalService.jcd_ta_key || globalService.globalParams?.jcd_ta_key;
    if (jcdTaKey !== undefined && jcdTaKey !== null && jcdTaKey !== '') {
      params.jcd_ta_key = jcdTaKey;
    }
    
    // Screen parameters
    const screenKey = filterParams.screen_key || globalService.screen_key || globalService.globalParams?.screen_key;
    if (screenKey !== undefined && screenKey !== null && screenKey !== '') {
      params.screen_key = screenKey;
    }
    
    const screenType = filterParams.screen_type || globalService.screen_type || globalService.globalParams?.screen_type;
    if (screenType !== undefined && screenType !== null && screenType !== '') {
      params.screen_type = screenType;
    }
    
    // Activity key
    const activityKey = filterParams.activity_key || globalService.activity_key || globalService.globalParams?.activity_key;
    if (activityKey !== undefined && activityKey !== null && activityKey !== '') {
      params.activity_key = activityKey;
    }
    
    // Lock status
    const isLocked = filterParams.is_locked || globalService.is_locked || globalService.globalParams?.is_locked;
    if (isLocked !== undefined && isLocked !== null && isLocked !== '') {
      params.is_locked = isLocked;
    }
    
    // Issue parameters - always include (even if empty) as per payload requirements
    // Get from all possible sources, default to empty string if not found
    const issueId = filterParams.issue_id || 
                    rowData.ISSUE_KEY || 
                    rowData.issue_id ||
                    globalService?.issue_id || 
                    globalService?.globalParams?.issue_id ||
                    '';
    params.issue_id = issueId !== undefined && issueId !== null ? issueId.toString() : '';
    
    const issueIdForTxSave = filterParams.issue_id_for_tx_save || 
                             rowData.ISSUE_KEY || 
                             rowData.issue_id_for_tx_save ||
                             globalService?.issue_id_for_tx_save || 
                             globalService?.globalParams?.issue_id_for_tx_save ||
                             '';
    params.issue_id_for_tx_save = issueIdForTxSave !== undefined && issueIdForTxSave !== null ? issueIdForTxSave.toString() : '';
    
    const issueShortDesc = filterParams.issue_short_desc || 
                          rowData.issue_short_desc ||
                          globalService?.issue_short_desc || 
                          globalService?.globalParams?.issue_short_desc ||
                          '';
    params.issue_short_desc = issueShortDesc !== undefined && issueShortDesc !== null ? issueShortDesc.toString() : '';
    
    // Scenario code - get from all sources, use common pattern if not found (like other services)
    const scenarioCode = filterParams.scenarioCode || 
                        filterParams.scenario_code || 
                        rowData.scenarioCode ||
                        rowData.scenario_code ||
                        globalService?.scenarioCode || 
                        globalService?.globalParams?.scenarioCode ||
                        generalConfigData?.scenarioCode ||
                        generalConfigData?.globalParams?.scenarioCode ||
                        'RAF'; // Common default pattern used in other services
    params.scenarioCode = scenarioCode !== undefined && scenarioCode !== null ? scenarioCode.toString() : 'RAF';
    
    // Is issue key enabled - get from all sources, use common pattern if not found (like other services)
    const isIssueKeyEnabled = filterParams.is_issue_key_enabled || 
                             rowData.is_issue_key_enabled ||
                             globalService?.is_issue_key_enabled || 
                             globalService?.globalParams?.is_issue_key_enabled ||
                             generalConfigData?.is_issue_key_enabled ||
                             generalConfigData?.globalParams?.is_issue_key_enabled ||
                             'N'; // Common default pattern used in other services
    params.is_issue_key_enabled = isIssueKeyEnabled !== undefined && isIssueKeyEnabled !== null ? isIssueKeyEnabled.toString() : 'N';
    
    // Filing key
    const filingKey = filterParams.filing_key || globalService.filing_key || globalService.globalParams?.filing_key;
    if (filingKey !== undefined && filingKey !== null && filingKey !== '') {
      params.filing_key = filingKey;
    }
    
    // CC key
    const ccKey = filterParams.cc_key || rowData.CC_KEY || globalService.cc_key || globalService.globalParams?.cc_key;
    if (ccKey !== undefined && ccKey !== null && ccKey !== '') {
      params.cc_key = ccKey;
    }

    // Include checkLockedbj from rowData and formData (NOT hardcoded)
    // Note: formData contains modalData (returnObj items) which has COMBINATION_KEY but not LEID, ME_CODE, etc.
    // Those fields come from rowData
    const scenarioKey = filterParams.scenario || rowData.SCENARIO || globalService.scenario || globalService.globalParams?.scenario;
    const checkLockedbj = [{
      "leid": rowData.LEID,
      "me_code": rowData.ME_CODE,
      "cdr_number": rowData.CDR_NO || rowData.MARS,
      "consol_group_key": rowData.CONSOL_GROUP_KEY,
      "reporting_period": rowData.REPORTING_PERIOD,
      "combination_key": formData[0]?.COMBINATION_KEY || rowData.COMBINATION_KEY,
      "scenario_key": scenarioKey ? parseInt(scenarioKey.toString()) : 0
    }];
    params.checkLockedbj = JSON.stringify(checkLockedbj);

    // Build form data payload (all params except action_code)
    // Include all params even if empty (for issue_id, issue_short_desc, issue_id_for_tx_save)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        // Include empty strings for issue-related fields as they're required in payload
        if (key === 'issue_id' || key === 'issue_short_desc' || key === 'issue_id_for_tx_save') {
          urlEncoded.set(key, params[key].toString());
        } else if (params[key] !== '') {
          // For other fields, only include if not empty
        urlEncoded.set(key, params[key].toString());
        }
      }
    });

    // URL with only action_code in query string
    const url = `${baseUrl}/saveJsonObject?action_code=91cnle`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    // POST request with form data in body
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Load trading partner data
   * @param baseUrl Base URL for API calls
   * @param filterParams Filter parameters
   * @param rowData Row data
   * @param globalService Global service data
   * @returns Observable with trading partner data
   */
  loadTradingPartnerData(
    baseUrl: string,
    filterParams: any = {},
    rowData: any = {},
    globalService: any = {}
  ): Observable<any> {
    // Build params from all sources with proper fallbacks (no hardcoded values)
    const params: { [key: string]: any } = {};
    
    // Action code (required - from user's payload)
    params.action_code = 'oh7ssv';
    
    // Scenario parameters
    const scenario = filterParams.scenario || rowData.SCENARIO || globalService.scenario || globalService.globalParams?.scenario;
    if (scenario !== undefined && scenario !== null && scenario !== '') {
      params.scenario = scenario;
      params.p_scenario = scenario;
    }
    
    // JCD key parameters
    const jcdKey = filterParams.jcd_key || rowData.JCD_KEY || globalService.jcd_key || globalService.globalParams?.jcd_key;
    if (jcdKey !== undefined && jcdKey !== null && jcdKey !== '') {
      params.jcd_key = jcdKey;
      params.p_jcd_key = jcdKey;
    }
    
    // Tax year
    const taxYear = filterParams.tax_year || rowData.TAX_YEAR || globalService.tax_year || globalService.globalParams?.tax_year;
    if (taxYear !== undefined && taxYear !== null && taxYear !== '') {
      params.tax_year = taxYear;
    }
    
    // Screen parameters
    const screenKey = filterParams.screen_key || globalService.screen_key || globalService.globalParams?.screen_key;
    if (screenKey !== undefined && screenKey !== null && screenKey !== '') {
      params.screen_key = screenKey;
    }
    
    const screenType = filterParams.screen_type || globalService.screen_type || globalService.globalParams?.screen_type;
    if (screenType !== undefined && screenType !== null && screenType !== '') {
      params.screen_type = screenType;
    }
    
    // Activity and JCD TA key
    const activityKey = filterParams.activity_key || globalService.activity_key || globalService.globalParams?.activity_key;
    if (activityKey !== undefined && activityKey !== null && activityKey !== '') {
      params.activity_key = activityKey;
    }
    
    const jcdTaKey = filterParams.jcd_ta_key || globalService.jcd_ta_key || globalService.globalParams?.jcd_ta_key;
    if (jcdTaKey !== undefined && jcdTaKey !== null && jcdTaKey !== '') {
      params.jcd_ta_key = jcdTaKey;
    }
    
    // CC key (can be comma-separated)
    const ccKey = filterParams.cc_key || rowData.CC_KEY || globalService.cc_key || globalService.globalParams?.cc_key;
    if (ccKey !== undefined && ccKey !== null && ccKey !== '') {
      params.cc_key = ccKey;
    }
    
    // Filing key
    const filingKey = filterParams.filing_key || globalService.filing_key || globalService.globalParams?.filing_key;
    if (filingKey !== undefined && filingKey !== null && filingKey !== '') {
      params.filing_key = filingKey;
    }
    
    // Lock status
    const isLocked = filterParams.is_locked || globalService.is_locked || globalService.globalParams?.is_locked;
    if (isLocked !== undefined && isLocked !== null && isLocked !== '') {
      params.is_locked = isLocked;
    }
    
    // Issue parameters
    const issueId = filterParams.issue_id || rowData.ISSUE_KEY || globalService.issue_id || globalService.globalParams?.issue_id;
    if (issueId !== undefined && issueId !== null && issueId !== '') {
      params.issue_id = issueId;
    }
    
    const issueIdForTxSave = filterParams.issue_id_for_tx_save || rowData.ISSUE_KEY || globalService.issue_id_for_tx_save || globalService.globalParams?.issue_id_for_tx_save;
    if (issueIdForTxSave !== undefined && issueIdForTxSave !== null && issueIdForTxSave !== '') {
      params.issue_id_for_tx_save = issueIdForTxSave;
    }
    
    const issueShortDesc = filterParams.issue_short_desc || globalService.issue_short_desc || globalService.globalParams?.issue_short_desc;
    if (issueShortDesc !== undefined && issueShortDesc !== null && issueShortDesc !== '') {
      params.issue_short_desc = issueShortDesc;
    }

    // Build query string parameters (NOT form data)
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.set(key, params[key].toString());
      }
    });

    // Use GET request with query string parameters
    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Load popup data for BEAT Type Consol Add
   * @param baseUrl Base URL for API calls
   * @param filterParams Filter parameters
   * @param rowData Row data
   * @param selectedBeatRow Selected beat row data
   * @param globalService Global service data
   * @returns Observable with popup data
   */
  loadPopUpData(
    baseUrl: string,
    filterParams: any = {},
    rowData: any = {},
    selectedBeatRow: any[] = [],
    globalService: any = {}
  ): Observable<any> {
    // Build params from all sources with proper fallbacks (no hardcoded values)
    const params: { [key: string]: any } = {};
    
    // Action code (required - from user's payload)
    params.action_code = '57rj34';
    
    // Scenario parameters
    const scenario = filterParams.scenario || rowData.SCENARIO || globalService.scenario || globalService.globalParams?.scenario;
    if (scenario !== undefined && scenario !== null && scenario !== '') {
      params.scenario = scenario;
      params.p_scenario = scenario;
    }
    
    // JCD key parameters
    const jcdKey = filterParams.jcd_key || rowData.JCD_KEY || globalService.jcd_key || globalService.globalParams?.jcd_key;
    if (jcdKey !== undefined && jcdKey !== null && jcdKey !== '') {
      params.jcd_key = jcdKey;
      params.p_jcd_key = jcdKey;
    }
    
    // Tax year
    const taxYear = filterParams.tax_year || rowData.TAX_YEAR || globalService.tax_year || globalService.globalParams?.tax_year;
    if (taxYear !== undefined && taxYear !== null && taxYear !== '') {
      params.tax_year = taxYear;
      params.p_tax_year = taxYear;
    }
    
    // Screen parameters
    const screenKey = filterParams.screen_key || globalService.screen_key || globalService.globalParams?.screen_key;
    if (screenKey !== undefined && screenKey !== null && screenKey !== '') {
      params.screen_key = screenKey;
    }
    
    const screenType = filterParams.screen_type || globalService.screen_type || globalService.globalParams?.screen_type;
    if (screenType !== undefined && screenType !== null && screenType !== '') {
      params.screen_type = screenType;
    }
    
    // Activity and JCD TA key
    const activityKey = filterParams.activity_key || globalService.activity_key || globalService.globalParams?.activity_key;
    if (activityKey !== undefined && activityKey !== null && activityKey !== '') {
      params.activity_key = activityKey;
    }
    
    const jcdTaKey = filterParams.jcd_ta_key || globalService.jcd_ta_key || globalService.globalParams?.jcd_ta_key;
    if (jcdTaKey !== undefined && jcdTaKey !== null && jcdTaKey !== '') {
      params.jcd_ta_key = jcdTaKey;
    }
    
    // CC key (can be comma-separated)
    const ccKey = filterParams.cc_key || rowData.CC_KEY || globalService.cc_key || globalService.globalParams?.cc_key;
    if (ccKey !== undefined && ccKey !== null && ccKey !== '') {
      params.cc_key = ccKey;
    }
    
    // Filing key
    const filingKey = filterParams.filing_key || globalService.filing_key || globalService.globalParams?.filing_key;
    if (filingKey !== undefined && filingKey !== null && filingKey !== '') {
      params.filing_key = filingKey;
    }
    
    // Lock status
    const isLocked = filterParams.is_locked || globalService.is_locked || globalService.globalParams?.is_locked;
    if (isLocked !== undefined && isLocked !== null && isLocked !== '') {
      params.is_locked = isLocked;
    }
    
    // Issue parameters
    const issueId = filterParams.issue_id || rowData.ISSUE_KEY || globalService.issue_id || globalService.globalParams?.issue_id;
    if (issueId !== undefined && issueId !== null && issueId !== '') {
      params.issue_id = issueId;
    }
    
    const issueIdForTxSave = filterParams.issue_id_for_tx_save || rowData.ISSUE_KEY || globalService.issue_id_for_tx_save || globalService.globalParams?.issue_id_for_tx_save;
    if (issueIdForTxSave !== undefined && issueIdForTxSave !== null && issueIdForTxSave !== '') {
      params.issue_id_for_tx_save = issueIdForTxSave;
    }
    
    const issueShortDesc = filterParams.issue_short_desc || globalService.issue_short_desc || globalService.globalParams?.issue_short_desc;
    if (issueShortDesc !== undefined && issueShortDesc !== null && issueShortDesc !== '') {
      params.issue_short_desc = issueShortDesc;
    }
    
    // Scenario code
    const scenarioCode = filterParams.scenarioCode || filterParams.scenario_code || globalService.scenarioCode || globalService.globalParams?.scenarioCode;
    if (scenarioCode !== undefined && scenarioCode !== null && scenarioCode !== '') {
      params.scenarioCode = scenarioCode;
    }
    
    // Is issue key enabled
    const isIssueKeyEnabled = filterParams.is_issue_key_enabled || globalService.is_issue_key_enabled || globalService.globalParams?.is_issue_key_enabled;
    if (isIssueKeyEnabled !== undefined && isIssueKeyEnabled !== null && isIssueKeyEnabled !== '') {
      params.is_issue_key_enabled = isIssueKeyEnabled;
    }
    
    // Selected beat row parameters
    if (selectedBeatRow && selectedBeatRow.length > 0) {
      const combinationKey = selectedBeatRow[0].COMBINATION_KEY;
      if (combinationKey !== undefined && combinationKey !== null && combinationKey !== '') {
        params.combination_key = combinationKey;
      }
      
      const gieKey = selectedBeatRow[0].GIE_KEY;
      if (gieKey !== undefined && gieKey !== null && gieKey !== '') {
        params.gie_key = gieKey;
      }
      
      const isPshipTier = selectedBeatRow[0].IS_PSHIP_TIER;
      if (isPshipTier !== undefined && isPshipTier !== null && isPshipTier !== '') {
        params.is_pship_tier = isPshipTier;
      }
      
      const selectedIssueId = selectedBeatRow[0].ISSUE_KEY;
      if (selectedIssueId !== undefined && selectedIssueId !== null && selectedIssueId !== '') {
        params.issue_id = selectedIssueId;
      }
    }

    // Build query string parameters (NOT form data)
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.set(key, params[key].toString());
      }
    });

    // Use GET request with query string parameters
    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Check lock status
   * @param baseUrl Base URL for API calls
   * @param filterParams Filter parameters
   * @param rowData Row data
   * @returns Observable with lock status
   */
  checkLockStatus(
    baseUrl: string,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const params: { [key: string]: any } = {
      'action_code': 'n10cpv',
      'p_scenario': filterParams.scenario || rowData.SCENARIO,
      'p_jcd_key': filterParams.jcd_key || rowData.JCD_KEY
    };

    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;
    return this.http.get<any>(url, { withCredentials: true });
  }
}
